"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { usePathname } from "next/navigation"

interface SurfCondition {
  waveHeight: string
  waveDirection: string
  swellPeriod: string
  windSpeed: string
  windDirection: string
  waterTemp: string
  tideLevel: string
  surfQuality: string
  kiteQuality?: string
  lastUpdated: string
}

interface UseSurfConditionsReturn {
  conditions: Record<string, SurfCondition> | null
  loading: boolean
  error: string | null
  refetch: () => void
}

interface Spot {
  id: string
  [key: string]: any
}

/**
 * Hook for fetching surf conditions by calling the forecast API for each spot
 * and extracting the current hour's data. This ensures 100% consistency with
 * the spot details page since it uses the exact same data source.
 * 
 * Automatically refreshes every 5 minutes
 * Only fetches conditions for spots that don't already have conditions
 */
export function useSurfConditions(spots: Spot[] = []): UseSurfConditionsReturn {
  const pathname = usePathname()
  const shouldFetch = !pathname?.startsWith("/auth") && spots.length > 0
  
  const [conditions, setConditions] = useState<Record<string, SurfCondition> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  
  // Track which spots we've already fetched conditions for (to avoid re-fetching)
  const fetchedSpotIdsRef = useRef<Set<string>>(new Set())
  
  // Create a stable array and string of spot IDs
  // Use a ref to track previous IDs and only update when they actually change
  const prevSpotIdsStringRef = useRef<string>('')
  const currentSpotIds = spots.map(spot => spot.id).sort()
  const currentSpotIdsString = currentSpotIds.join(',')
  
  // Update ref if IDs changed, otherwise keep previous value
  if (currentSpotIdsString !== prevSpotIdsStringRef.current) {
    prevSpotIdsStringRef.current = currentSpotIdsString
  }
  
  const spotIdsArray = currentSpotIds
  const spotIdsString = prevSpotIdsStringRef.current

  // Wrap fetchConditions in useCallback to prevent unnecessary re-renders
  // Only fetch conditions for spots we don't already have
  const fetchConditions = useCallback(async (forceRefetch: boolean = false) => {
    if (!shouldFetch || spotIdsArray.length === 0) return
    
    // Determine which spots need fetching
    const spotsToFetch = forceRefetch 
      ? spotIdsArray 
      : spotIdsArray.filter(id => !fetchedSpotIdsRef.current.has(id))
    
    if (spotsToFetch.length === 0) {
      // All spots already have conditions, nothing to fetch
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      // Fetch forecast only for spots that need it
      const forecastPromises = spotsToFetch.map(async (spotId) => {
        try {
          const response = await fetch(`/api/surf-forecast?spotId=${spotId}`)
          if (!response.ok) {
            console.warn(`Failed to fetch forecast for spot ${spotId}`)
            return { spotId, data: null }
          }
          
          const result = await response.json()
          
          if (!result.success || !result.data || !result.data.data || !Array.isArray(result.data.data) || result.data.data.length === 0) {
            return { spotId, data: null }
          }
          
          // Extract today's forecast (first day)
          const todayForecast = result.data.data[0]
          
          // Validate that todayForecast exists and has hourly data
          if (!todayForecast || !todayForecast.hourly || !Array.isArray(todayForecast.hourly) || todayForecast.hourly.length === 0) {
            return { spotId, data: null }
          }
          
          // Find the current hour
          // The API returns ISO timestamps in the spot's local timezone (e.g., 2026-03-08T07:00)
          // We want to find the hour that matches the current time in the spot's timezone
          const now = new Date()
          const currentHour = now.getHours()
          
          // Try to find exact hour match first (simple and usually correct for local users)
          let currentHourData = todayForecast.hourly.find((h: any) => h.hour === currentHour)
          
          // If no exact match (maybe early morning/late night), find the closest previous hour
          if (!currentHourData) {
            currentHourData = todayForecast.hourly
              .filter((h: any) => h.hour <= currentHour)
              .sort((a: any, b: any) => b.hour - a.hour)[0]
          }
          
          // Last resort: use first available hour
          if (!currentHourData && todayForecast.hourly.length > 0) {
            currentHourData = todayForecast.hourly[0]
          }
          
          if (!currentHourData) {
            return { spotId, data: null }
          }
          
          // Convert forecast data format to conditions format
          const condition: SurfCondition = {
            waveHeight: currentHourData.waveHeightDisplay || `${currentHourData.waveHeight} ft`,
            waveDirection: currentHourData.waveDirection,
            swellPeriod: `${currentHourData.wavePeriod}s`,
            windSpeed: currentHourData.windSpeedDisplay || `${currentHourData.windSpeed} mph`,
            windDirection: currentHourData.windDirection,
            waterTemp: currentHourData.temperatureDisplay || `${currentHourData.temperature}°F`,
            tideLevel: "Mid",
            surfQuality: currentHourData.quality,
            kiteQuality: currentHourData.kiteQuality,
            lastUpdated: new Date().toISOString(),
          }
          
          return { spotId, data: condition }
        } catch (err) {
          console.error(`Error fetching forecast for spot ${spotId}:`, err)
          return { spotId, data: null }
        }
      })
      
      const results = await Promise.all(forecastPromises)
      
      // Build conditions map, merging with existing conditions
      setConditions(prevConditions => {
        const conditionsMap: Record<string, SurfCondition> = { ...prevConditions }
        results.forEach(result => {
          if (result.data) {
            conditionsMap[result.spotId] = result.data
            // Mark this spot as fetched
            fetchedSpotIdsRef.current.add(result.spotId)
          }
        })
        return conditionsMap
      })
    } catch (err) {
      console.error("Error fetching conditions:", err)
      setError(err instanceof Error ? err.message : "Failed to fetch conditions")
    } finally {
      setLoading(false)
    }
  }, [shouldFetch, spotIdsArray]) // Dependencies: shouldFetch and spotIdsArray (stable reference)

  // Fetch conditions when spot IDs change (new spots added) or refetch is triggered
  useEffect(() => {
    // Only fetch if we have new spots that don't have conditions yet
    const hasNewSpots = spotIdsArray.some(id => !fetchedSpotIdsRef.current.has(id))
    if (hasNewSpots || refetchTrigger > 0) {
      fetchConditions(refetchTrigger > 0) // Force refetch if triggered manually
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotIdsString, refetchTrigger]) // Only depend on spotIdsString and refetchTrigger, not fetchConditions

  // Set up polling interval to refresh conditions every 5 minutes
  useEffect(() => {
    if (!shouldFetch) return

    const interval = setInterval(() => {
      // Force refetch all conditions every 5 minutes
      fetchedSpotIdsRef.current.clear() // Clear cache to force re-fetch
      setRefetchTrigger(prev => prev + 1)
    }, 5 * 60 * 1000) // 5 minutes

    return () => clearInterval(interval)
  }, [shouldFetch])

  const refetch = () => {
    fetchedSpotIdsRef.current.clear() // Clear cache to force re-fetch
    setRefetchTrigger(prev => prev + 1)
  }

  return {
    conditions,
    loading: shouldFetch ? loading : false,
    error,
    refetch,
  }
}